/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// useCompletionChecklist
//
// Standalone sibling of useGuidedChecklist, used by the COMPLETED-TREATMENT
// wrap-up hub (CompletedTreatmentHub). It loads the lead's current treatment
// checklist, narrowed to the CURRENT stage (TREATMENT_STARTED).
//
// SCOPE: the 2026-05-28 relaxation (whole treatment, every stage) is
// intentionally RE-NARROWED per user request 2026-10-20. Showing every stage
// lumped completed New-Lead / Consultation items into "Done" (24 Done on a lead
// with 9 Treatment-Started items). The hub now mirrors the attended popup: only
// groups whose template `applies_to_statuses` includes TREATMENT_STARTED, via the
// same `filterGroupsByTreatment` gate useGuidedChecklist uses. It still does NOT
// build a post-completion (review / recall) set — that is a future phase.
//
//   - KEEPS treatment-specific scoping (treatment_item.id + lead_treatment row).
//   - KEEPS the tasks-v2 feature gate (early empty return, no API when disabled).
//   - KEEPS the active/done bucketing (today / later / done). The `done` bucket
//     is "out of the active queue" (completed + skipped + paused); use
//     `counts` (or checklistOutcome helpers) for the split numbers.
//   - A lead whose current stage has no matching template/group gets the empty
//     state (templatesEmpty) — no crash.
//
// Why a standalone hook (not a param on useGuidedChecklist):
//   useGuidedChecklist powers the live attended popup and MUST NOT regress.
//   We reuse its already-exported `filterGroupsByTreatment` helper.
//
// Differences from useGuidedChecklist:
//   - Stage is TREATMENT_STARTED (not CONSULTATION_ATTENDED)
//   - No spawn-retry loop (tasks already exist by the time treatment completes)
//
// Same return shape:
//   { loading, initialLoading, templatesEmpty, groups, upNext,
//     templatePreviewItems, allDoneCount, counts, refresh }
//
// Data sources (identical to useGuidedChecklist):
//   1. /api/v1/practices/{p}/leads/{l}/task-checklists/  → spawned tasks
//   2. /api/v1/practices/{p}/task-templates/?limit=200   → template metadata
// -----------------------------------------------------------------------------
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import { filterGroupsByTreatment } from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { countChecklistOutcomes } from './checklistOutcome';

// The hub's "current treatment" stage. Picks the active lead_treatment row AND
// is the stage templates must apply to (applies_to_statuses) — see header.
const ACTIVE_TREATMENT_STATUS = 'TREATMENT_STARTED';

// ---------------------------------------------------------------------------
// Helpers (mirror useGuidedChecklist)
// ---------------------------------------------------------------------------

/** Midnight at the start of today (local). */
function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

/** One millisecond before midnight at the end of today (local). */
function endOfToday() {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
}

/** True when the checklist item is completed, skipped, or paused — all out of the active queue. */
export function isItemCompleted(item) {
    if (!item) return false;
    if (item.completed_at != null) return true;
    if (item.status === 'completed') return true;
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.SKIPPED) return true;
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED) return true;
    return false;
}

// ---------------------------------------------------------------------------
// Flat-item builder
// ---------------------------------------------------------------------------

// NOTE: stage + treatment narrowing already happened (filterGroupsByTreatment);
// every group that survived contributes its items.
export function buildFlatItems(groups) {
    const items = [];
    let globalOrder = 0;

    groups.forEach((group) => {
        const tasks = group.tasks || [];
        tasks.forEach((task) => {
            const checklistItems = task.checklist_items || [];
            if (checklistItems.length === 0) return;

            const sortedItems = [...checklistItems].sort(
                (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
            );

            sortedItems.forEach((item) => {
                const resolvedType = item.item_type || item.action_type || '';
                const resolvedTitle = item.title || item.name || '';
                if (!resolvedType && !resolvedTitle) return;

                items.push({
                    itemId: item.id ?? item.checklist_item_id,
                    id: item.id ?? item.checklist_item_id,
                    taskId: task.id,
                    taskTitle: task.title,
                    templateId: group.template_id,
                    templateName: group.template_name,
                    item_type: item.item_type || item.action_type || '',
                    title: item.title || item.name || '',
                    description: item.description || '',
                    completed_at: item.completed_at || null,
                    status: item.status || null,
                    sort_order: item.sort_order ?? null,
                    due_at: task.due_at || null,
                    completion_state: item.completion_state || null,
                    is_completed: item.is_completed ?? null,
                    paused_until: item.paused_until || null,
                    done: isItemCompleted(item),
                    templateOrder: globalOrder,
                    raw: item,
                    rawTask: task,
                });
                globalOrder += 1;
            });
        });
    });

    return items;
}

// ---------------------------------------------------------------------------
// Group builder
// ---------------------------------------------------------------------------

export function buildGroups(flatItems) {
    const todayEnd = endOfToday();

    const today = [];
    const later = [];
    const done = [];

    flatItems.forEach((item) => {
        if (item.done) {
            done.push(item);
            return;
        }
        if (!item.due_at) {
            later.push(item);
            return;
        }
        const d = new Date(parseApiDate(item.due_at));
        if (d <= todayEnd) {
            today.push(item);
        } else {
            later.push(item);
        }
    });

    today.sort((a, b) => new Date(parseApiDate(a.due_at)) - new Date(parseApiDate(b.due_at)));

    return { today, later, done };
}

// ---------------------------------------------------------------------------
// Up Next selector
// ---------------------------------------------------------------------------

export function selectUpNext(flatItems) {
    const todayStart = startOfToday();
    const todayEnd = endOfToday();
    const incomplete = flatItems.filter((i) => !i.done);
    if (incomplete.length === 0) return null;

    const overdue = incomplete
        .filter((i) => i.due_at && new Date(parseApiDate(i.due_at)) < todayStart)
        .sort((a, b) => new Date(parseApiDate(a.due_at)) - new Date(parseApiDate(b.due_at)));
    if (overdue.length > 0) return overdue[0];

    const dueToday = incomplete
        .filter((i) => {
            if (!i.due_at) return false;
            const d = new Date(parseApiDate(i.due_at));
            return d >= todayStart && d <= todayEnd;
        })
        .sort((a, b) => new Date(parseApiDate(a.due_at)) - new Date(parseApiDate(b.due_at)));
    if (dueToday.length > 0) return dueToday[0];

    return incomplete.sort((a, b) => a.templateOrder - b.templateOrder)[0];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object} params
 * @param {string|number} params.practiceId
 * @param {string|number} params.leadId
 * @param {object}        params.leadDetail  – reconstructed lead object (carries
 *                        lead_treatments[] so the treatment filter resolves).
 *
 * @returns {{
 *   loading: boolean,
 *   initialLoading: boolean,
 *   templatesEmpty: boolean,
 *   groups: { today: object[], later: object[], done: object[] },
 *   upNext: object|null,
 *   templatePreviewItems: object[],
 *   allDoneCount: number,
 *   counts: { done: number, skipped: number, paused: number, total: number },
 *   refresh: function,
 * }}
 */
export default function useCompletionChecklist({ practiceId, leadId, leadDetail }) {
    const [loading, setLoading] = useState(true);
    const hasDataRef = useRef(false);
    // Tracks which leadId we have already sent the "treatment_item missing" Sentry
    // warning for, so a single hook instance never fires captureMessage more than
    // once per lead even when fetchAll is invoked multiple times (e.g. cache busts,
    // effect re-runs caused by leadDetail reference changes).
    const sentryWarnedLeadRef = useRef(null);
    const [templatesEmpty, setTemplatesEmpty] = useState(false);
    const [groups, setGroups] = useState({ today: [], later: [], done: [] });
    const [upNext, setUpNext] = useState(null);
    const [templatePreviewItems, setTemplatePreviewItems] = useState([]);
    const [allDoneCount, setAllDoneCount] = useState(0);

    const templatesUpdatedAt = useSelector((s) => s?.taskTemplate?.templatesUpdatedAt || 0);

    const fetchAll = useCallback(async ({ bustCache = false } = {}) => {
        if (!practiceId || !leadId) {
            setLoading(false);
            setTemplatesEmpty(true);
            return;
        }

        // Feature-gate: never fire API calls for practices without tasks-v2.
        // MUST early-return empty (no API calls) — mirrors useGuidedChecklist.
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setLoading(false);
            setTemplatesEmpty(true);
            return;
        }

        setLoading(true);
        try {
            const [checklistsRes, tplRes] = await Promise.all([
                callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`,
                    'GET', null, false,
                    bustCache ? { bustCache: true } : {}
                ),
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
            ]);

            let groupsData = [];
            if (Array.isArray(checklistsRes?.data)) groupsData = checklistsRes.data;
            else if (Array.isArray(checklistsRes?.data?.results)) groupsData = checklistsRes.data.results;

            let tplData = [];
            if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
            else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;

            // Resolve the lead's active treatment id for the treatment-specific
            // filter. Priority: TREATMENT_STARTED → first treatment.
            // treatment_data.treatment_item.id is the master TreatmentItem ID,
            // which matches applies_to_treatment_items[].id on templates.
            const allTreatments = leadDetail?.lead_treatments || [];
            const matchedTreatment =
                allTreatments.find((t) => t.status === ACTIVE_TREATMENT_STATUS) ||
                allTreatments[0];
            // RCG-2 defensive read: matchedTreatment.treatment_data can arrive as a
            // bare string (taskBrief-style rows) rather than an object. Guard it to
            // object-or-null before reading .treatment_item?.id or taking
            // Object.keys — a string would otherwise yield per-character digit
            // keys ("0".."9") in the Sentry payload instead of [].
            const _td = matchedTreatment?.treatment_data;
            const _tdObj = (_td && typeof _td === 'object' && !Array.isArray(_td)) ? _td : null;
            const leadTreatmentId = _tdObj?.treatment_item?.id ?? null;
            const matchedLeadTreatmentId = matchedTreatment?.id ?? null;
            // When treatment_item.id is absent (V2-flat API returns a lighter
            // treatment_data shape without nested treatment_item for some leads),
            // leadTreatmentId stays null.  We gracefully degrade by showing only
            // GENERIC templates (applies_to_treatment_items = []) — see
            // passesTreatmentFilter below.  Log once per lead; never on every
            // fetchAll invocation to avoid Sentry spam.
            if (matchedTreatment && leadTreatmentId == null && sentryWarnedLeadRef.current !== leadId) {
                sentryWarnedLeadRef.current = leadId;
                Sentry.captureMessage('useCompletionChecklist: leadTreatmentItemId missing on lead_treatment', {
                    level: 'warning',
                    tags: { context: 'completion_checklist_treatment_id' },
                    extra: { practiceId, leadId, treatmentDataKeys: Object.keys(_tdObj || {}) },
                });
            }

            // Graceful degrade: when leadTreatmentId is null (V2-flat API omits
            // treatment_item), generic templates (empty applies_to_treatment_items)
            // still pass so the checklist stays useful.  Treatment-scoped templates
            // are skipped — they can't be matched without the treatment item id.
            const passesTreatmentFilter = (t) => {
                const tItems = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
                if (tItems.length === 0) return true; // generic — always show
                if (leadTreatmentId == null) return false; // treatment-scoped, no id to match
                return tItems.some((ti) => String(ti?.id) === String(leadTreatmentId));
            };

            // Same gate as useGuidedChecklist: the template must apply to the
            // current stage (TREATMENT_STARTED), be active, have items, and match
            // the lead's treatment. None → empty state (templatesEmpty).
            const appliesToStage = (t) =>
                (Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []).includes(ACTIVE_TREATMENT_STATUS);
            const applicableTemplatesExist = tplData.some((t) => {
                const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                const isActive = t.is_active !== false;
                return appliesToStage(t) && items.length > 0 && isActive && passesTreatmentFilter(t);
            });

            if (!applicableTemplatesExist) {
                setTemplatesEmpty(true);
                setGroups({ today: [], later: [], done: [] });
                setUpNext(null);
                setTemplatePreviewItems([]);
                setAllDoneCount(0);
                return;
            }

            const tplTreatmentMap = new Map();
            tplData.forEach((t) => {
                tplTreatmentMap.set(
                    String(t.id),
                    Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []
                );
            });

            // Reuse the exported helper from useGuidedChecklist for its stage +
            // treatment + lead_treatment-row scoping, with the stage set to
            // TREATMENT_STARTED (the helper's `attendedStatus` option).
            const tplStatusMap = new Map();
            tplData.forEach((t) => {
                tplStatusMap.set(String(t.id), Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []);
            });

            const filteredGroupsData = filterGroupsByTreatment(groupsData, {
                tplStatusMap,
                tplTreatmentMap,
                leadTreatmentId,
                matchedLeadTreatmentId,
                attendedStatus: ACTIVE_TREATMENT_STATUS,
            });

            const flatItems = buildFlatItems(filteredGroupsData);

            setTemplatesEmpty(false);

            // Progress-based all-done detection (BE may omit completed items).
            if (flatItems.length === 0) {
                let progressTotal = 0;
                let progressCompleted = 0;
                // Count progress across the stage+treatment-matching groups.
                filteredGroupsData.forEach((group) => {
                    const p = group.progress || {};
                    progressTotal += (p.total || 0);
                    progressCompleted += (p.completed || 0);
                });
                if (progressTotal > 0 && progressCompleted >= progressTotal) {
                    setAllDoneCount(progressTotal);
                    setGroups({ today: [], later: [], done: [] });
                    setUpNext(null);
                    setTemplatePreviewItems([]);
                    return;
                }
            }
            setAllDoneCount(0);

            // Template preview when no spawned tasks match yet.
            if (flatItems.length === 0) {
                const applicable = tplData
                    .filter((t) => {
                        const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                        return appliesToStage(t) && items.length > 0 && t.is_active !== false && passesTreatmentFilter(t);
                    })
                    .sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity));
                const previewItems = [];
                applicable.forEach((tpl) => {
                    const sortedCi = [...(tpl.checklist_items || [])].sort(
                        (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
                    );
                    sortedCi.forEach((ci, idx) => {
                        previewItems.push({
                            id: `preview-${tpl.id}-${idx}`,
                            title: ci.title || ci.name || '',
                            description: ci.description || '',
                            item_type: ci.item_type || ci.action_type || '',
                            due_at: null,
                            completed_at: null,
                            _preview: true,
                            _templateId: tpl.id,
                            _templateName: tpl.name || '',
                        });
                    });
                });
                setTemplatePreviewItems(previewItems);
            } else {
                setTemplatePreviewItems([]);
            }

            const builtGroups = buildGroups(flatItems);
            const nextItem = selectUpNext(flatItems);

            setGroups(builtGroups);
            setUpNext(nextItem);
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'completion_checklist_fetch' } });
            setTemplatesEmpty(true);
            setGroups({ today: [], later: [], done: [] });
            setUpNext(null);
            setTemplatePreviewItems([]);
            setAllDoneCount(0);
        } finally {
            hasDataRef.current = true;
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, leadId, JSON.stringify((leadDetail?.lead_treatments || []).map((t) => ({ id: t.id, s: t.status, ti: t.treatment_data?.treatment_item?.id ?? null })))]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll, templatesUpdatedAt]);

    const counts = useMemo(() => countChecklistOutcomes(groups), [groups]);

    return {
        loading,
        initialLoading: loading && !hasDataRef.current,
        templatesEmpty,
        groups,
        upNext,
        templatePreviewItems,
        allDoneCount,
        counts,
        refresh: fetchAll,
    };
}
